import {
  Chat16,
  CheckmarkOutline16,
  Close20,
  EarthFilled16,
  Menu20,
  Notification16,
  OverflowMenuHorizontal16,
} from '@carbon/icons-react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind-styled-components';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import {
  CustomerPortalPageLight,
  CustomerPortalPageType,
} from 'api/customer-portal/types';
import { Project } from 'api/project';
import { ReactComponent as ProjectSwitcherIconSvg } from 'assets/svgs/project-switcher.svg';
import { CpBody01Primary } from 'components/atoms/customer-portal/typography';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { NOTIFICATIONS_PAGE } from '../slice';
import { CpApprovalsAndCsat } from './cp-approvals-and-csat/cp-approvals-and-csat';
import { CpButton } from './cp-button';
import { CpMenu } from './modal/cp-menu';
import { CpPopover } from './modal/cp-popover';
import { CpNotificationsContent } from './widgets/cp-notifications-content';

export interface CpNavBarProps {
  pages: CustomerPortalPageLight[];
  onPageClick: (p: CustomerPortalPageLight) => void;
  title: React.ReactNode;
  activePageRefId?: string;
  projects: Project[];
  onProjectChange: (projectId: number) => void;
  disableProjectSwitcher?: boolean;
  disableNotifications?: boolean;
  selectedProject?: Project;
  onSlackIntegrationClick?: () => void;
  onNotificationSettingsClick?: () => void;
  disableApprovalsAndCsat?: boolean;
  dontShowNotificationSettings?: boolean;
  showInviteButton?: boolean;
  onShareClick?: () => void;
}

export const CpRouteConstants = {
  home: '/',
  notifications: '/notifications',
  accountOverview: '/overview',
  projects: '/projects',
  chat: '/chats',
} as const;

const ProjectSwitcherButton = tw.button`
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      md:cp-mr-[var(--cp-spacing-05)]
      cp-h-full
`;
const MoreTabsButton = tw.button`
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      cp-h-full
      cp-p-[var(--spacing-04)]
`;

const Span = styled.span`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${CpBody01Primary} {
    color: white !important;
    margin-right: var(--cp-spacing-03);
  }
`;

const ProjectSwitcherIcon: React.FC = () => (
  <ProjectSwitcherButton>
    <ProjectSwitcherIconSvg />
  </ProjectSwitcherButton>
);

const CpNavbarItemLabelOrIcon: React.FC<{
  page: CustomerPortalPageLight;
}> = ({ page }) => {
  if (!page) {
    return <></>;
  }
  const { pageType, pageName } = page;

  if (page === NOTIFICATIONS_PAGE) {
    const content = (
      <>
        <Notification16 />
      </>
    );

    return content;
  }

  switch (pageType) {
    case CustomerPortalPageType.Home:
      return (
        <>
          <p className="cp-mb-0">{pageName}</p>
        </>
      );

    case CustomerPortalPageType.Chat:
      return (
        <>
          <Chat16 className="cp-hidden cp-w-0 md:cp-block md:cp-w-auto" />
          <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
        </>
      );

    case CustomerPortalPageType.AccountOverview:
      return (
        <>
          <EarthFilled16 className="cp-hidden cp-w-0 md:cp-block md:cp-w-auto" />
          <p className="md:cp-hidden md:cp-w-0 cp-mb-0">{pageName}</p>
        </>
      );

    default:
      return <p className="cp-mb-0">{pageName}</p>;
  }
};

const Nav = tw.nav`cp-flex cp-items-center cp-justify-between rl-cp-nav-bar-wrapper cp-bg-[var(--black-100)] cp-sticky cp-top-0 cp-z-[100] cp-px-[calc(calc(100%_-_1440px)/2)]  cp-mx-auto`;
const TitleWrapper = tw.div`rl-cp-nav-bar-title-wrapper cp-w-auto cp-h-full cp-p-[var(--spacing-05)__var(--spacing-06)] cp-cursor-pointer md:cp-pl-[var(--cp-spacing-06)] cp-pl-[var(--cp-spacing-05)] lg:cp-pr-0`;

const LgRoutesList = tw.ul<{ end?: boolean }>`
    rl-cp-routes-wrapper
    cp-hidden
    md:cp-flex
    ${(props) => (props?.end ? 'cp-justify-end' : 'cp-justify-start')}
    cp-pl-0
    cp-items-center
    cp-h-[52px]
    cp-w-full
    cp-mb-0
`;

const SmRoutesList = tw.ul<{ visible: boolean }>`
${(props) =>
  props.visible ? 'cp-flex cp-translate-x-0' : 'cp-translate-x-[-10000px]'}
    cp-fixed
    cp-left-0
    cp-z-[10000000]
    cp-top-[var(--spacing-09)]
    rl-cp-routes-wrapper
    md:cp-hidden
    cp-flex
    cp-justify-start
    cp-items-end
    cp-w-full
    cp-mb-0
    cp-flex-col
    cp-bg-black
    cp-h-[calc(100vh__-__48px)]
    cp-transition-transform
    cp-duration-500
    cp-px-[var(--spacing-05)]
`;

const NavItem = tw.li<{ isActive?: boolean }>`
    ${(props) =>
      props.isActive
        ? 'lg:cp-border-solid lg:cp-border-[var(--blue-60)]'
        : 'lg:cp-border-solid lg:cp-border-transparent'}
    cp-text-[var(--white-100)]
    cp-border-0
    md:cp-border-b-4
    md:cp-pt-4
    md:cp-border-transparent
    md:hover:cp-border-[var(--cp-gray-20)]


    cp-cursor-pointer
    lg:cp-mr-[var(--cp-spacing-05)]
    md:cp-mr-[var(--cp-spacing-03)]
    cp-flex
    cp-items-center
    cp-justify-end
    lg:cp-justify-center
    md:cp-w-auto
    cp-w-full
    cp-p-[var(--spacing-04)]
    cp-h-\[58px]


    cp-border-b-[1px]
    cp-border-[var(--cp-gray-20)]
    cp-border-solid


    lg:hover:cp-border-0
    lg:hover:cp-border-b-4
    lg:hover:cp-border-[var(--gray-20)]
    lg:hover:cp-border-solid
    cp-whitespace-nowrap
`;

const HamMenuButton = tw.button`
      md:cp-hidden
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      cp-px-[var(--spacing-05)]
`;

const IconButton = tw.li`
      cp-flex
      cp-items-center
      cp-justify-center
      cp-cursor-pointer
      cp-bg-transparent
      cp-text-[var(--white-100)]
      cp-border-none
      cp-outline-none
      cp-px-[var(--spacing-05)]
      cp-pl-[var(--spacing-04)]
      lg:cp-mr-[var(--cp-spacing-05)]
      md:cp-mr-[var(--cp-spacing-03)]
      cp-h-[40px]
`;

const ShareButton = styled(CpButton)`
  margin-right: var(--cp-spacing-06);

  @media (max-width: 480px) {
    margin-right: var(--cp-spacing-05);
  }
`;

export const CpNavBarRenderer: React.FC<CpNavBarProps> = ({
  pages,
  onPageClick,
  activePageRefId,
  projects,
  onProjectChange,
  disableProjectSwitcher,
  selectedProject,
  title,
  disableNotifications,
  onSlackIntegrationClick,
  onNotificationSettingsClick,
  disableApprovalsAndCsat,
  dontShowNotificationSettings,
  showInviteButton,
  onShareClick,
}) => {
  const isResponsive = useIsResponsive();
  const isTab = useIsTablet();
  const [isHamMenuVisible, setHamMenuVisiblity] = useState(false);
  const onNavItemClick = useCallback(
    (page: CustomerPortalPageLight) => {
      onPageClick(page);
      setHamMenuVisiblity(false);
    },
    [onPageClick],
  );

  const filteredPages = useMemo(
    () => pages.filter((e) => e?.pageType === CustomerPortalPageType.Custom),
    [pages],
  );

  const pagesLookup = useMemo(() => _.keyBy(pages, 'routePath'), [pages]);

  const homePage = useMemo(
    () => pagesLookup[CpRouteConstants.home],
    [pagesLookup],
  );

  /* overview page */
  const overviewPage = useMemo(
    () => pagesLookup[CpRouteConstants.accountOverview],
    [pagesLookup],
  );

  /* chat page */
  const chatPage = useMemo(
    () => pagesLookup[CpRouteConstants.chat],
    [pagesLookup],
  );

  const onTitleClick = useCallback(() => {
    onNavItemClick(homePage);
  }, [homePage, onNavItemClick]);

  const projectSwitcherComponent = useMemo(
    () =>
      projects?.length > 1 ? (
        disableProjectSwitcher ? (
          <Span>
            <CpBody01Primary className="cp-hidden lg:cp-flex">
              {selectedProject?.projectName?.slice?.(
                0,
                Math.min(selectedProject?.projectName?.length, 20),
              )}
              {selectedProject && selectedProject?.projectName?.length > 20 ? (
                '...'
              ) : (
                <></>
              )}
            </CpBody01Primary>
            <ProjectSwitcherIcon />
          </Span>
        ) : (
          <CpPopover
            placement="bottomLeft"
            closeOnSelect
            trigger={['click', 'hover']}
            content={
              <CpMenu
                selectedKeys={
                  selectedProject
                    ? [selectedProject?.projectId?.toString()]
                    : []
                }
              >
                {projects.map((e) => (
                  <CpMenu.Item
                    onClick={() => onProjectChange(e?.projectId)}
                    key={e?.projectId?.toString()}
                  >
                    {isResponsive
                      ? e?.projectName
                      : e?.projectName?.length > 25
                      ? `${e?.projectName?.slice(0, 25)}...`
                      : e?.projectName}
                  </CpMenu.Item>
                ))}
              </CpMenu>
            }
          >
            <Span>
              <CpBody01Primary className="cp-hidden lg:cp-flex">
                {selectedProject?.projectName?.slice?.(
                  0,
                  Math.min(selectedProject?.projectName?.length, 20),
                )}
                {selectedProject &&
                selectedProject?.projectName?.length > 20 ? (
                  '...'
                ) : (
                  <></>
                )}
              </CpBody01Primary>
              <ProjectSwitcherIcon />
            </Span>
          </CpPopover>
        )
      ) : (
        <></>
      ),
    [
      disableProjectSwitcher,
      isResponsive,
      onProjectChange,
      projects,
      selectedProject,
    ],
  );

  const approvalsAndCsatComponent = useMemo(() => {
    if (disableApprovalsAndCsat) {
      return (
        <NavItem>
          <CheckmarkOutline16 />
        </NavItem>
      );
    }

    return (
      <CpApprovalsAndCsat>
        <NavItem>
          <CheckmarkOutline16 />
        </NavItem>
      </CpApprovalsAndCsat>
    );
  }, [disableApprovalsAndCsat]);

  const firstHalf = useMemo(() => {
    if (isTab) {
      if (filteredPages?.length > 2) {
        return filteredPages?.slice(0, 2);
      }
    }

    if (filteredPages?.length > 6) {
      return filteredPages.slice(0, 6);
    }

    return filteredPages;
  }, [filteredPages, isTab]);

  const secondHalf = useMemo(() => {
    if (isTab) {
      if (filteredPages?.length > 2) {
        return filteredPages?.slice(2);
      }
    }

    if (filteredPages?.length > 6) {
      return filteredPages.slice(6);
    }

    return [];
  }, [filteredPages, isTab]);

  const notificationsContent = useMemo(() => {
    const content = (
      <IconButton>
        <CpNavbarItemLabelOrIcon page={NOTIFICATIONS_PAGE} />
      </IconButton>
    );

    if (disableNotifications) {
      return content;
    }

    return (
      <CpPopover
        trigger={['click']}
        content={
          <CpNotificationsContent
            dontShowNotificationSettings={dontShowNotificationSettings}
            onNotificationSettingsClick={onNotificationSettingsClick}
            onSlackIntegrationClick={onSlackIntegrationClick}
          />
        }
        getPopupContainer={() =>
          document.getElementById('cp-modal-root') as HTMLElement
        }
      >
        <span>{content}</span>
      </CpPopover>
    );
  }, [
    disableNotifications,
    dontShowNotificationSettings,
    onNotificationSettingsClick,
    onSlackIntegrationClick,
  ]);

  return (
    <Nav>
      <TitleWrapper onClick={onTitleClick}>{title}</TitleWrapper>
      <NavItem
        className="lg:cp-ml-[var(--cp-spacing-05)] !cp-justify-start cp-border-b-transparent md:cp-justify-center cp-pl-0 lg:cp-pl-[var(--cp-spacing-04)] cp-hidden md:cp-flex"
        isActive={activePageRefId === homePage?.pageReferenceId}
        key={homePage?.pageReferenceId}
        onClick={onTitleClick}
      >
        <CpNavbarItemLabelOrIcon page={homePage} />
      </NavItem>

      <LgRoutesList className="cp-ml-[var(--cp-spacing-03)]">
        {firstHalf.map((e) => (
          <NavItem
            key={e?.pageReferenceId}
            isActive={activePageRefId === e?.pageReferenceId}
            onClick={() => onNavItemClick(e)}
          >
            <CpNavbarItemLabelOrIcon page={e} />
          </NavItem>
        ))}
        {isResponsive ? (
          <></>
        ) : secondHalf?.length ? (
          <CpPopover
            content={
              <CpMenu
                selectedKeys={
                  activePageRefId ? [activePageRefId?.toString()] : []
                }
              >
                {secondHalf.map((e) => (
                  <CpMenu.Item
                    key={e?.pageReferenceId}
                    onClick={() => onNavItemClick(e)}
                  >
                    {e?.pageName}
                  </CpMenu.Item>
                ))}
              </CpMenu>
            }
          >
            <NavItem>
              <MoreTabsButton>
                <OverflowMenuHorizontal16 />
              </MoreTabsButton>
            </NavItem>
          </CpPopover>
        ) : (
          <></>
        )}
      </LgRoutesList>
      <LgRoutesList end>
        <Tooltip title={overviewPage?.pageName}>
          <NavItem
            isActive={activePageRefId === overviewPage?.pageReferenceId}
            onClick={() => onNavItemClick(overviewPage)}
          >
            <CpNavbarItemLabelOrIcon page={overviewPage} />
          </NavItem>
        </Tooltip>

        <Tooltip title={chatPage?.pageName}>
          <NavItem
            isActive={activePageRefId === chatPage?.pageReferenceId}
            onClick={() => onNavItemClick(chatPage)}
          >
            <CpNavbarItemLabelOrIcon page={chatPage} />
          </NavItem>
        </Tooltip>

        {notificationsContent}

        {!isResponsive ? approvalsAndCsatComponent : <></>}
        {!isResponsive ? projectSwitcherComponent : <></>}
      </LgRoutesList>

      <SmRoutesList visible={isHamMenuVisible}>
        {filteredPages.map((e) => (
          <NavItem
            key={e?.pageReferenceId}
            isActive={activePageRefId === e?.pageReferenceId}
            onClick={() => onNavItemClick(e)}
          >
            <CpNavbarItemLabelOrIcon page={e} />
          </NavItem>
        ))}
        {[overviewPage, chatPage]
          .filter((e) => !!e)
          .map((e) => (
            <NavItem
              isActive={activePageRefId === e?.pageReferenceId}
              key={e?.pageReferenceId}
              onClick={() => onNavItemClick(e)}
            >
              <CpNavbarItemLabelOrIcon page={e} />
            </NavItem>
          ))}
      </SmRoutesList>
      {isResponsive ? notificationsContent : <></>}

      {isResponsive ? approvalsAndCsatComponent : <></>}
      {isResponsive ? projectSwitcherComponent : <></>}
      <HamMenuButton
        onClick={() => {
          setHamMenuVisiblity((p) => !p);
        }}
      >
        {isHamMenuVisible ? <Close20 /> : <Menu20 />}
      </HamMenuButton>
      {showInviteButton ? (
        <ShareButton
          onClick={onShareClick}
          cpType={CustomerPortalButtonType.Primary}
        >
          <FormattedMessage id="share" />
        </ShareButton>
      ) : (
        <></>
      )}
    </Nav>
  );
};
